/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';

import { times } from 'lodash';
import { compose, setDisplayName } from 'recompose';

import Retracked from 'js/lib/retracked';

import { Grid, breakpoints } from '@coursera/cds-core';

import SkillSetFilterResultsA11yAnnouncer from 'bundles/enterprise-legacy-learner-home/components/single-program/SkillSetFilterResultsA11yAnnouncer';
import type { FilterSelections } from 'bundles/enterprise-legacy-learner-home/components/single-program/SkillSetFiltersHOCDGS';
import { getFilterTrackingData } from 'bundles/enterprise-legacy-learner-home/utils/ProgramHomeUtils';
import type { SkillSetListQuery_EnterpriseTargetSkillProfileSummariesV1Resource_byProgram_elements as TargetSkillProficiencySummary } from 'bundles/program-home/components/enterprise-home/__generated__/SkillSetListQuery';
import { SkillSetCardV3, SkillSetCardV3Placeholder } from 'bundles/program-home/components/multiprogram/SkillSetCardV3';

type BaseContainerProps = {
  skillsets: TargetSkillProficiencySummary[];
  skillSetLength?: number;
  filterSelections?: FilterSelections;
  loading?: boolean;
};

type PropsFromCaller = BaseContainerProps &
  (
    | { isOrgScoped: true; orgSlug: string; programId?: never; programSlug?: never }
    | { isOrgScoped?: false; programId: string; programSlug: string }
  );

type PlaceholderProps = {
  skillSetLength?: number;
};

export type Props = PropsFromCaller;

const styles = {
  skillSetsContainer: css`
    margin-bottom: var(--cds-spacing-300);
  `,
  gridItem: css`
    margin-bottom: var(--cds-spacing-300);

    ${breakpoints.down('sm')} {
      margin-bottom: var(--cds-spacing-200);
    }
    ${breakpoints.down('xs')} {
      margin-bottom: var(--cds-spacing-400);
    }
  `,
};

export function SkillSetsContainerV2Placeholder({ skillSetLength }: PlaceholderProps) {
  return (
    <div css={styles.skillSetsContainer}>
      <Grid container spacing={{ md: 24, sm: 16 }}>
        {times(skillSetLength || 4, (value) => (
          <Grid key={value} css={styles.gridItem} item xs={12} sm={6}>
            <SkillSetCardV3Placeholder />
          </Grid>
        ))}
      </Grid>
    </div>
  );
}

export function SkillSetsContainerV2({
  skillSetLength = 0,
  filterSelections,
  skillsets,
  loading,
  ...scopeProps
}: Props) {
  if (loading) return <SkillSetsContainerV2Placeholder />;
  if (skillSetLength) {
    const filterTrackingData = getFilterTrackingData(filterSelections);

    return (
      <div css={styles.skillSetsContainer}>
        <SkillSetFilterResultsA11yAnnouncer
          filteredSkillSetCount={skillSetLength}
          filterSelections={filterSelections}
          loading={loading} // We don't want to update the count until all skillsets are retrieved
        />
        <Grid container spacing={{ md: 24, sm: 16 }} data-e2e="SkillSetsContainerV2">
          {skillsets.map((skillWithCollection, idx) => (
            <Grid key={skillWithCollection.id} css={styles.gridItem} item xs={12} sm={6}>
              <SkillSetCardV3
                skillSetOrder={idx}
                skillSummary={skillWithCollection}
                filterTrackingData={filterTrackingData}
                {...(scopeProps.isOrgScoped
                  ? { isOrgScoped: true, orgSlug: scopeProps.orgSlug }
                  : { programSlug: scopeProps.programSlug })}
              />
            </Grid>
          ))}
        </Grid>
      </div>
    );
  }

  return null;
}

export const enhance = compose<Props, PropsFromCaller>(
  setDisplayName('SkillSetsContainerV2'),
  Retracked.createTrackedContainer<PropsFromCaller>((props) =>
    props.isOrgScoped ? { orgSlug: props.orgSlug } : { programId: props.programId }
  )
);

export default enhance(SkillSetsContainerV2);
